import { ArrowPathIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import {
	GQLDesignPageFragment,
	GQLOperationalSettingsFragment,
	GQLProductFragment,
	GQLProductSideAvailableDecorationType,
	GQLProductSideSideType,
	GQLStoreFragment,
} from '@theprintbar/graphql';
import {
	Merch,
	capitalise,
	cloneDeep,
	cn,
	decorationMethodIsSamedayCompatible,
	emptyDecorationMeta,
	getAvailableSideDecoration,
	getDecorationBGColour,
	getDecorationContent,
	getDesignReadySides,
	getSamedayName,
} from '@theprintbar/shared';
import { Button, Modal, ModalPosition, OutlineButton } from '@theprintbar/ui';
import { useCallback, useEffect, useState } from 'react';
import { TransparentButton } from '../../..';
import { clearMerchSession } from '../../pages/Design/utils/merchStateStorage';
import type { CreativeEditorInstance } from '../CreativeEditor';
import { editorTools } from '../CreativeEditor/editorTools';
import SidePreview from './SideComponents/SidePreview';

interface ProductSidesProps {
	editor: CreativeEditorInstance;
	activeSide: GQLProductSideSideType;
	content: GQLDesignPageFragment;
	updatingDesigner: boolean;
	updatingPrice: boolean;
	setActiveSide: (side: GQLProductSideSideType) => void;
	merch?: Merch;
	webDomain: string;
	product: GQLProductFragment;
	operationalSettings: GQLOperationalSettingsFragment;
	calculateStitches: () => void;
	updateMerch: (merch: Merch) => void;
	setMobileOpen: (open: boolean) => void;
	onRefresh?: () => void;
	store?: GQLStoreFragment | null;
	crew?: boolean;
	freeEdit?: boolean;
}

export function ProductSides({
	editor,
	merch,
	product,
	content,
	operationalSettings,
	updateMerch,
	webDomain,
	activeSide,
	updatingDesigner,
	updatingPrice,
	calculateStitches,
	setActiveSide,
	setMobileOpen,
	onRefresh,
	store,
	freeEdit,
	crew,
}: ProductSidesProps) {
	const sides = getDesignReadySides(product, !store);
	const [switchingType, setSwitchingType] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [newType, setNewType] = useState<GQLProductSideAvailableDecorationType | null>(null);
	const [available, setAvailable] = useState(false);
	const [activePanel, setActivePanel] = useState<'main' | 'method' | 'colour'>('main');

	const activeDecoration = merch?.decorations.find(d => d.side === activeSide);
	const samedayName = getSamedayName();

	useEffect(() => {
		if (!store && merch?.sameday && [GQLProductSideSideType.Left, GQLProductSideSideType.Right].includes(activeSide)) {
			setActiveSide(GQLProductSideSideType.Front);
		}
	}, [merch, switchingType]);

	const handleTypeChange = useCallback(
		(newType: GQLProductSideAvailableDecorationType, available: boolean, colours?: number) => {
			if (!merch) return;
			if (!available) {
				setNewType(newType);
				setAvailable(available);
				setIsModalOpen(true);
				return;
			}
			proceedWithTypeChange(newType, colours);
		},
		[editor, merch, updateMerch, activeSide, setSwitchingType],
	);

	const proceedWithTypeChange = useCallback(
		async (newType: GQLProductSideAvailableDecorationType, colours?: number) => {
			if (!merch) return;
			const merchIndex = merch.decorations.findIndex(d => d.side === activeSide);
			let newMerch = cloneDeep(merch);
			if (merchIndex !== -1) {
				newMerch.decorations[merchIndex].type = newType;
				newMerch.decorations[merchIndex].meta.requestedColours = colours || 1;
			} else {
				newMerch.decorations.push({
					side: activeSide,
					type: newType,
					artwork: new Blob([]),
					meta: emptyDecorationMeta,
				});
			}

			if (merch.sameday && !decorationMethodIsSamedayCompatible(product, newType)) {
				newMerch.sameday = false;
			}

			updateMerch(newMerch);
			editorTools.setDecorationMethod(editor, newType);
			setSwitchingType(false);
			setIsModalOpen(false);
		},
		[editor, merch, updateMerch, activeSide, setSwitchingType],
	);

	const handleColourChange = useCallback(
		async (newCount: number) => {
			if (!merch) return;
			const merchIndex = merch.decorations.findIndex(d => d.side === activeSide);
			let newMerch = cloneDeep(merch);
			if (merchIndex === -1) return;

			const dec = newMerch.decorations[merchIndex];

			if (!dec.meta.requestedColours && dec.meta.colours?.length === newCount) return;

			newMerch.decorations[merchIndex].meta.requestedColours = newCount;

			updateMerch(newMerch);
		},
		[editor, merch, updateMerch, activeSide],
	);

	const handleRefresh = useCallback(() => {
		// eslint-disable-next-line no-alert
		if (window.confirm('Are you sure you want to start over?\nThis will clear all your settings/designs.')) {
			//Refresh the page
			clearMerchSession();
			onRefresh?.();
			const newUrl = window.location.origin + window.location.pathname;
			window.history.replaceState(null, '', newUrl);
			window.location.reload();
		}
	}, [merch, updateMerch]);

	const setMainDecoration = useCallback(
		(isMain: boolean) => {
			if (!merch) return;
			let newMerch = cloneDeep(merch);
			newMerch.decorations = newMerch.decorations.map(d => ({ ...d, isMainDecoration: d.side === activeSide && isMain }));

			updateMerch(newMerch);
		},
		[editor, merch, updateMerch, activeSide],
	);
	return (
		<div>
			<div className="flex w-full justify-between items-center lg:mb-2">
				<h5 className="text-xs uppercase">My Decorations</h5>
				<h5 className="uppercase text-xs whitespace-nowrap"></h5>
				{freeEdit && (
					<TransparentButton type="button" onClick={handleRefresh} icon={ArrowPathIcon}>
						<h5 className="uppercase text-xs whitespace-nowrap">Start Over</h5>
					</TransparentButton>
				)}
			</div>
			<div className="relative border border-blue rounded-3xl overflow-hidden">
				{updatingDesigner && (
					<div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-10">
						<img src="/images/loading.gif" alt="Cartoon person" className="w-24" />
						<div className="text-center">
							<h5 className="uppercase text-blue">Loading...</h5>
						</div>
					</div>
				)}
				<div className="flex bg-white">
					{sides.map((sideFrag, index) => {
						const { side } = sideFrag;
						const thisDecoration = merch?.decorations.find(d => d.side === side);
						const nextDecoration = merch?.decorations.find(d => d.side === sides[index + 1]?.side);
						const hasError = !!thisDecoration?.error;
						const isCurrent = side === activeSide;
						const nextActive = sides[index + 1]?.side === activeSide;
						const nextHasDecoration =
							!!nextDecoration?.artwork?.size &&
							!!getAvailableSideDecoration({ side: sides[index + 1], sameday: merch?.sameday && !store });
						const availableDec = getAvailableSideDecoration({ side: sideFrag, sameday: merch?.sameday && !store });
						const hasDecoration = !!thisDecoration?.artwork?.size && availableDec;
						const lastSide = sides[sides.length - 1].side === side;

						const nextColour = nextActive
							? 'bg-white'
							: nextHasDecoration
							? getDecorationBGColour(nextDecoration.type)
							: 'bg-white-pearl';
						const nextColourOpacity = !nextHasDecoration
							? 'bg-opacity-20'
							: nextDecoration?.type === GQLProductSideAvailableDecorationType.Dtf
							? 'bg-opacity-60'
							: 'bg-opacity-40';
						const thisColour = isCurrent
							? 'bg-white'
							: hasDecoration
							? getDecorationBGColour(thisDecoration.type)
							: 'bg-white-pearl';

						return !!freeEdit || (hasDecoration && thisDecoration.type) ? (
							<div key={side} className={cn('transition flex-1 flex', nextColour, nextColourOpacity)}>
								<button
									onClick={() => {
										setSwitchingType(false);
										setActiveSide(side);
										setActivePanel('main');
									}}
									className={cn(
										'relative group flex-1 rounded-tr-3xl bg-white border-blue border-b overflow-hidden',
										isCurrent && 'border-b-transparent',
										!lastSide && 'border-r',
									)}
									key={side}
									disabled={isCurrent || !availableDec}
								>
									<div
										className={cn(
											'transition-colors h-full flex items-center px-4 py-1.5 lg:py-3 text-left text-blue',
											thisColour,
											!hasDecoration
												? 'bg-opacity-20'
												: thisDecoration.type === GQLProductSideAvailableDecorationType.Dtf
												? 'bg-opacity-60'
												: 'bg-opacity-40',
										)}
									>
										<div
											className={cn(
												'transition text-left text-blue',
												!isCurrent && availableDec && 'group-hover:scale-105',
												!availableDec && 'group-hover:text-grey/30 text-grey/30',
											)}
										>
											<h6 className={cn('transition text-sm lg:text-base lg:leading-4')}>{capitalise(side)}</h6>
											<p className="text-xs lg:hidden">
												{thisDecoration?.type && hasDecoration
													? getDecorationContent(thisDecoration?.type, operationalSettings).title.split(' ')[0]
													: !availableDec
													? 'Unavailable'
													: 'Blank'}
											</p>
											<p className="hidden lg:block text-xs">
												{thisDecoration?.type && hasDecoration
													? getDecorationContent(thisDecoration?.type, operationalSettings).title
													: !availableDec
													? 'Unavailable'
													: 'Blank'}
											</p>
										</div>
										{hasError && <ExclamationCircleIcon className="text-red w-6 h-6 absolute top-4 right-4" />}
									</div>
								</button>
							</div>
						) : null;
					})}
				</div>
				<SidePreview
					activeSide={activeSide}
					content={content}
					activeDecoration={activeDecoration}
					merch={merch}
					product={product}
					webDomain={webDomain}
					operationalSettings={operationalSettings}
					editor={editor}
					updatingPrice={updatingPrice}
					setMainDecoration={setMainDecoration}
					handleTypeChange={handleTypeChange}
					handleColourChange={handleColourChange}
					switchingType={switchingType}
					setSwitchingType={setSwitchingType}
					calculateStitches={calculateStitches}
					openDesigner={setMobileOpen}
					store={store}
					crew={crew}
					freeEdit={freeEdit}
					activePanel={activePanel}
					setActivePanel={setActivePanel}
				/>
			</div>
			<Modal open={isModalOpen} className="w-2/3 lg:w-1/3 h-auto min-h-0" position={ModalPosition.Center}>
				<p>
					You had {samedayName} selected, we don't offer this for Embroidery, DTF or Screen Printing. If you'd like your order{' '}
					{samedayName}, select Digital Printing and hit the {samedayName} toggle.
				</p>
				<div className="flex items-center justify-end gap-4 mt-4">
					<Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
					<OutlineButton
						onClick={() => {
							if (newType !== null) {
								proceedWithTypeChange(newType);
							}
						}}
					>
						Proceed
					</OutlineButton>
				</div>
			</Modal>
		</div>
	);
}
